// The READ side of the local cache — port of Flutter's local_repo.dart.
//
// Every function returns the SAME SHAPE the matching REST endpoint returns, so
// callers cannot tell where the data came from. That is what makes the
// local-first swap in api/localFirst.js a two-line change per endpoint instead
// of a rewrite of every screen.
//
// Devices need enriching: `devices_installed` holds the raw install row, while
// the REST list endpoints return it joined with names. So each device row gets
//   LocationName    from location      (by location_id)
//   RoomName        from rooms         (by room_id, null for common devices)
//   DeviceTypeName  from device_type   (by device_id)
//   Outputs/Inputs  from device_type   (by device_id)
//   favourite       from favourite_devices
// added only where the row doesn't already carry them.
//
// ⚠️ `Outputs`/`Inputs` COME FROM THE TYPE, NOT THE INSTALL ROW — on REST too,
// where they arrive through the same join. The install row has neither, so
// without this a cached door sensor looks like an ordinary device with no
// outputs, and core/devices/sensorState (which needs both numbers) cannot tell
// it is a sensor. The room list then drew it as a control card, offline only,
// which is the sort of split that gets diagnosed as a caching bug rather than a
// missing column.
//
// ⚠️ WHAT THE CACHE STILL CANNOT ANSWER: the per-user `SortOrder`. The list
// endpoints return `UserDeviceMapping.SortOrder` — the order the customer
// arranged — while the sink endpoint returns `DevicesInstalled.SortOrder`,
// which is 0 on every row. There is no sink endpoint for UserDeviceMapping (see
// syncService's PLAN), so an offline list keeps insertion order. Sorting it is
// still correct and still stable; there is simply nothing to sort BY until the
// backend offers that table.
//
// Everything is defensive — any failure returns null so the caller falls back to
// REST. A broken cache must degrade to "slower", never to "broken".

import { dbSupported } from './schema';
import { getAll, getAllByIndex } from './idb';

const rawOf = (rec) => (rec && rec.raw && typeof rec.raw === 'object' ? { ...rec.raw } : {});

async function safe(fn) {
  if (!dbSupported()) return null;
  try {
    return await fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[LocalRepo]', (e && e.message) || e);
    return null;
  }
}

// ── lookup tables, read once per device query ──────────────────────────────
async function lookups() {
  const [locs, rooms, types, favs] = await Promise.all([
    getAll('location'), getAll('rooms'), getAll('device_type'), getAll('favourite_devices'),
  ]);

  const locationNames = new Map((locs || []).map((r) => [r.location_id, r.location_name || '']));
  const roomNames = new Map((rooms || []).map((r) => [r.room_id, r.room_name || '']));
  const typeNames = new Map((types || []).map((r) => {
    const j = rawOf(r);
    const name = ['DeviceTypeName', 'DeviceType', 'TypeName', 'Name', 'DeviceName']
      .map((k) => j[k]).find((v) => v != null && String(v).trim() !== '');
    return [r.device_id, name ? String(name) : ''];
  }));
  // The channel counts, straight off the cached DeviceType row — the same two
  // columns the REST join supplies.
  const typeChannels = new Map((types || []).map((r) => {
    const j = rawOf(r);
    return [r.device_id, { Outputs: j.Outputs, Inputs: j.Inputs }];
  }));
  const favourites = new Set((favs || []).map((r) => r.unique_number).filter(Boolean));

  return {
    locationNames, roomNames, typeNames, typeChannels, favourites,
  };
}

function enrich(records, {
  locationNames, roomNames, typeNames, typeChannels, favourites,
}) {
  return records.map((rec) => {
    const d = rawOf(rec);
    if (d.LocationName == null) d.LocationName = locationNames.get(rec.location_id) ?? null;
    if (d.RoomName == null) {
      d.RoomName = (rec.room_id == null || rec.room_id === 0)
        ? null
        : (roomNames.get(rec.room_id) ?? null);
    }
    if (d.DeviceTypeName == null) d.DeviceTypeName = typeNames.get(rec.device_id) ?? null;
    const ch = typeChannels && typeChannels.get(rec.device_id);
    if (ch) {
      if (d.Outputs == null) d.Outputs = ch.Outputs;
      if (d.Inputs == null) d.Inputs = ch.Inputs;
    }
    d.favourite = favourites.has(rec.unique_number) ? 1
      : ((d.favourite === 1 || d.favourite === '1') ? 1 : 0);
    return d;
  });
}

// ── the REST-shaped reads ──────────────────────────────────────────────────
// Each returns null when the cache can't answer, so the caller falls back.

export const getLocations = (userId) => safe(async () => {
  const [maps, locs] = await Promise.all([
    getAll('user_location_mapping'), getAll('location'),
  ]);
  const mine = new Set((maps || [])
    .filter((m) => String(m.user_id) === String(userId))
    .map((m) => m.location_id));
  return (locs || []).filter((l) => mine.has(l.location_id)).map(rawOf);
});

export const getRooms = (userId, locationId) => safe(async () => {
  const [maps, rooms] = await Promise.all([
    getAll('location_rooms_mapping'), getAll('rooms'),
  ]);
  const locId = parseInt(locationId, 10);
  const mine = new Set((maps || [])
    .filter((m) => String(m.user_id) === String(userId) && m.location_id === locId)
    .map((m) => m.room_id));
  return (rooms || []).filter((r) => mine.has(r.room_id)).map(rawOf);
});

// Common devices belong to a location but no room.
export const getCommonDevices = (locationId) => safe(async () => {
  const recs = await getAllByIndex('devices_installed', 'by_location', parseInt(locationId, 10));
  const common = (recs || []).filter((r) => r.room_id == null || r.room_id === 0);
  return enrich(common, await lookups());
});

export const getRoomDevices = (roomId) => safe(async () => {
  const recs = await getAllByIndex('devices_installed', 'by_room', parseInt(roomId, 10));
  return enrich(recs || [], await lookups());
});

export const getAllDevices = () => safe(async () => {
  const recs = await getAll('devices_installed');
  return enrich(recs || [], await lookups());
});

export const getDeviceDetail = (uniqueNumber) => safe(async () => {
  const recs = await getAllByIndex('devices_installed', 'by_uno', String(uniqueNumber));
  if (!recs || recs.length === 0) return null;
  const [one] = enrich([recs[0]], await lookups());
  return one;
});

export const getOutputs = (uniqueNumber) => safe(async () => {
  const recs = await getAllByIndex('output_details', 'by_uno', String(uniqueNumber));
  return (recs || [])
    .sort((a, b) => (a.output_index ?? 0) - (b.output_index ?? 0))
    .map(rawOf);
});

export const getFavouriteDevices = () => safe(async () => {
  const recs = await getAll('favourite_devices');
  return (recs || []).map(rawOf);
});
